import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';
import { Typography, Box, Link } from '@mui/material';

interface MarkdownRendererProps {
  content: string;
}

const MarkdownRenderer = ({ content }: MarkdownRendererProps) => {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkBreaks]}
      rehypePlugins={[rehypeRaw]}
      components={{
        p: ({ children, ...props }) => {
          const { ref, ...restProps } = props as { ref?: unknown; [key: string]: unknown };
          return (
            <Typography
              variant='body1'
              {...restProps}
              sx={{
                lineHeight: 1.6,
                mb: 1,
              }}
            >
              {children}
            </Typography>
          );
        },
        h1: ({ children, ...props }) => {
          const { ref, ...restProps } = props as { ref?: unknown; [key: string]: unknown };
          return (
            <Typography
              variant='h4'
              {...restProps}
              sx={{ mb: 2, mt: 2, fontWeight: 'bold' }}
            >
              {children}
            </Typography>
          );
        },
        h2: ({ children, ...props }) => {
          const { ref, ...restProps } = props as { ref?: unknown; [key: string]: unknown };
          return (
            <Typography
              variant='h5'
              {...restProps}
              sx={{ mb: 1.5, mt: 1.5, fontWeight: 'bold' }}
            >
              {children}
            </Typography>
          );
        },
        h3: ({ children, ...props }) => {
          const { ref, ...restProps } = props as { ref?: unknown; [key: string]: unknown };
          return (
            <Typography
              variant='h6'
              {...restProps}
              sx={{ mb: 1, mt: 1, fontWeight: 'bold' }}
            >
              {children}
            </Typography>
          );
        },
        h4: ({ children, ...props }) => {
          const { ref, ...restProps } = props as { ref?: unknown; [key: string]: unknown };
          return (
            <Typography
              variant='subtitle1'
              {...restProps}
              sx={{ mb: 1, mt: 1, fontWeight: 'bold' }}
            >
              {children}
            </Typography>
          );
        },
        h5: ({ children, ...props }) => {
          const { ref, ...restProps } = props as { ref?: unknown; [key: string]: unknown };
          return (
            <Typography
              variant='subtitle2'
              {...restProps}
              sx={{ mb: 0.5, mt: 0.5, fontWeight: 'bold' }}
            >
              {children}
            </Typography>
          );
        },
        h6: ({ children, ...props }) => {
          const { ref, ...restProps } = props as { ref?: unknown; [key: string]: unknown };
          return (
            <Typography
              variant='body2'
              {...restProps}
              sx={{ mb: 0.5, mt: 0.5, fontWeight: 'bold' }}
            >
              {children}
            </Typography>
          );
        },
        ul: ({ children, ...props }) => {
          const { ref, ...restProps } = props as { ref?: unknown; [key: string]: unknown };
          return (
            <Box component='ul' {...restProps} sx={{ pl: 2, mb: 1 }}>
              {children}
            </Box>
          );
        },
        ol: ({ children, ...props }) => {
          const { ref, ...restProps } = props as { ref?: unknown; [key: string]: unknown };
          return (
            <Box component='ol' {...restProps} sx={{ pl: 2, mb: 1 }}>
              {children}
            </Box>
          );
        },
        li: ({ children, ...props }) => {
          const { ref, ...restProps } = props as { ref?: unknown; [key: string]: unknown };
          return (
            <Box component='li' {...restProps} sx={{ mb: 0.5 }}>
              {children}
            </Box>
          );
        },
        table: ({ children, ...props }) => {
          const { ref, ...restProps } = props as { ref?: unknown; [key: string]: unknown };
          return (
            <Box
              component='table'
              {...restProps}
              sx={{
                width: '100%',
                borderCollapse: 'collapse',
                mb: 2,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              {children}
            </Box>
          );
        },
        th: ({ children, ...props }) => {
          const { ref, ...restProps } = props as { ref?: unknown; [key: string]: unknown };
          return (
            <Box
              component='th'
              {...restProps}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                p: 1,
                bgcolor: 'grey.100',
                fontWeight: 'bold',
                textAlign: 'left',
              }}
            >
              {children}
            </Box>
          );
        },
        td: ({ children, ...props }) => {
          const { ref, ...restProps } = props as { ref?: unknown; [key: string]: unknown };
          return (
            <Box
              component='td'
              {...restProps}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                p: 1,
              }}
            >
              {children}
            </Box>
          );
        },
        code: ({ children, ...props }) => {
          const { ref, ...restProps } = props as { ref?: unknown; [key: string]: unknown };
          return (
            <Box
              component='code'
              {...restProps}
              sx={{
                bgcolor: 'grey.100',
                px: 0.5,
                py: 0.25,
                borderRadius: 0.5,
                fontFamily: 'monospace',
                fontSize: '0.875em',
              }}
            >
              {children}
            </Box>
          );
        },
        pre: ({ children, ...props }) => {
          const { ref, ...restProps } = props as { ref?: unknown; [key: string]: unknown };
          return (
            <Box
              component='pre'
              {...restProps}
              sx={{
                bgcolor: 'grey.100',
                p: 2,
                borderRadius: 1,
                overflow: 'auto',
                mb: 1,
                fontFamily: 'monospace',
              }}
            >
              {children}
            </Box>
          );
        },
        blockquote: ({ children, ...props }) => {
          const { ref, ...restProps } = props as { ref?: unknown; [key: string]: unknown };
          return (
            <Box
              component='blockquote'
              {...restProps}
              sx={{
                borderLeft: '4px solid',
                borderColor: 'primary.main',
                pl: 2,
                ml: 0,
                mb: 1,
                fontStyle: 'italic',
              }}
            >
              {children}
            </Box>
          );
        },
        a: ({ children, ...props }) => {
          const { ref, ...restProps } = props as { ref?: unknown; href?: string; [key: string]: unknown };
          return (
            <Link
              href={restProps.href}
              sx={{
                color: 'primary.main',
                textDecoration: 'underline',
                '&:hover': {
                  textDecoration: 'none',
                },
              }}
              {...restProps}
            >
              {children}
            </Link>
          );
        },
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

export default MarkdownRenderer;

